import React from 'react';
import { Typography, SelectMenu } from '@scale/scaleui';
import Select from 'corp/components/UI/Select';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import {
  QualificationType,
  SELECTABLE_QUALIFICATION_TYPES,
} from '@scale/shared/types/qualification/types';
import { WorkerSkillDimension, WorkerSkillDimensionQualityCategory } from '@scale/shared-types';
import { IconButton } from 'react-scale/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import copyToClipboard from 'copy-to-clipboard';
import { QualificationSelectOption } from '@scale/shared/types/params/course';

interface ISelectQualification {
  onQualificationTypeChange?: (qualification: QualificationType) => void;
  onWorkerSkillChange: (workerSkillUpdate: any) => void;
  value?: QualificationSelectOption;
  qualificationType?: QualificationType;
  requireStatus?: boolean;
}

export const updateSkillFromSelectionChange = (
  skillSelect: QualificationSelectOption,
  value: any,
) => {
  const updatedSkill = skillSelect;
  if (Object.values(WorkerSkillDimension).includes(value)) {
    if (!updatedSkill.workerSkill) {
      updatedSkill.workerSkill = {
        label: '',
        value: '',
        dimension: value,
      };
    } else {
      updatedSkill.workerSkill.dimension = value;
    }
  } else {
    updatedSkill.workerSkill = {
      ...updatedSkill.workerSkill,
      ...value,
    };
  }
  return updatedSkill;
};

const SelectQualification: React.FC<ISelectQualification> = ({
  value,
  onQualificationTypeChange,
  onWorkerSkillChange,
  qualificationType,
  requireStatus,
}) => {
  const qualType = qualificationType ?? value?.qualificationType;
  const workerSkill = value?.workerSkill;

  const fetchWorkerSkills = async (search: string) => {
    try {
      const query = {
        search,
      };
      const results = await apiFetch(null, `corp-api/find/workerSkills`, { query });
      const options = results.workerSkills.map((workerSkill: { name: any; id: any }) => {
        return {
          label: `${workerSkill.name}`,
          value: workerSkill.id,
          name: workerSkill.name,
        };
      });
      return {
        options,
      };
    } catch (e) {
      defaultApiFetchErrorHandler(e, { handleHttp: true });
      return [];
    }
  };

  return (
    <div className="scaleui scale-theme scale-tailwind">
      {!qualType && onQualificationTypeChange && (
        <div>
          <Typography.Paragraph1>Qualification Type</Typography.Paragraph1>
          <SelectMenu
            className="w-[400px]"
            options={SELECTABLE_QUALIFICATION_TYPES.map(t => ({ id: t, name: t }))}
            setSelected={onQualificationTypeChange}
            selectedOptionId={qualType ?? QualificationType.WorkerSkill}
          />
        </div>
      )}
      {qualType === QualificationType.WorkerSkill && (
        <div>
          <Typography.Paragraph1>Worker Skill Name or ID</Typography.Paragraph1>
          <Select
            onChange={onWorkerSkillChange}
            value={workerSkill}
            loadOptions={fetchWorkerSkills.bind(this)}
            ignoreCase={true}
            placeholder="Worker Skill Name or ID"
          />
          <Typography.Paragraph1 className="mt-2">Worker Skill Dimension</Typography.Paragraph1>
          <SelectMenu
            options={Object.values(WorkerSkillDimension).map(dimension => ({
              id: dimension,
              name: dimension,
            }))}
            setSelected={onWorkerSkillChange}
            selectedOptionId={workerSkill?.dimension ?? WorkerSkillDimension.Overall}
          />
          {requireStatus && (
            <div>
              <Typography.Paragraph1 className="mt-2">Worker Skill Status</Typography.Paragraph1>
              <SelectMenu
                options={Object.values(WorkerSkillDimensionQualityCategory).map(t => ({
                  id: t,
                  name: t,
                }))}
                setSelected={onWorkerSkillChange}
                selectedOptionId={
                  workerSkill?.status ?? WorkerSkillDimensionQualityCategory.SelfReported
                }
              />
            </div>
          )}
          {workerSkill?.value && (
            <div className="flex flex-row mt-2">
              <Typography.Paragraph1 className="mr-2">Copy ID to Clipboard</Typography.Paragraph1>
              <IconButton size="small" onClick={() => copyToClipboard(workerSkill.value)}>
                <FontAwesomeIcon icon="copy" color="lightgrey" />
              </IconButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectQualification;
